import React, { useEffect, useRef, useContext } from "react";
import gsap from "gsap";
import {
  FlightOutlined, FlightTakeoffOutlined, AccountBalanceOutlined,
  LocalMallOutlined, ShoppingBagOutlined, MosqueOutlined,
} from "@mui/icons-material";
import { LanguageContext } from "./LanguageContext";

const locationData = {
  en: {
    eyebrow:     "Getting Around",
    title:       "Prime Location",
    description: "Masar sits at the heart of Sharjah with direct access to Emirates Road — putting the best of the UAE within easy reach.",
    locations: [
      { Icon: FlightOutlined,         time: "20", unit: "min", place: "Dubai International Airport" },
      { Icon: FlightTakeoffOutlined,  time: "15", unit: "min", place: "Sharjah Airport"             },
      { Icon: AccountBalanceOutlined, time: "12", unit: "min", place: "Sharjah University City"     },
      { Icon: LocalMallOutlined,      time: "12", unit: "min", place: "Alzahia Mall"                },
      { Icon: ShoppingBagOutlined,    time: "5",  unit: "min", place: "Mall Soyo"                   },
      { Icon: MosqueOutlined,         time: "7",  unit: "min", place: "Sharjah Mosque"              },
    ],
  },
  ar: {
    eyebrow:     "المسافات والتنقل",
    title:       "موقع استراتيجي",
    description: "يقع مسار في قلب الشارقة مع وصول مباشر لشارع الإمارات — يجعل أبرز وجهات الإمارات في متناول يدك.",
    locations: [
      { Icon: FlightOutlined,         time: "20", unit: "د", place: "مطار دبي الدولي"         },
      { Icon: FlightTakeoffOutlined,  time: "15", unit: "د", place: "مطار الشارقة"            },
      { Icon: AccountBalanceOutlined, time: "12", unit: "د", place: "مدينة الشارقة الجامعية"  },
      { Icon: LocalMallOutlined,      time: "12", unit: "د", place: "الزاهية مول"             },
      { Icon: ShoppingBagOutlined,    time: "5",  unit: "د", place: "مول سويّو"               },
      { Icon: MosqueOutlined,         time: "7",  unit: "د", place: "مسجد الشارقة"            },
    ],
  },
};

const LocationSection = () => {
  const { language } = useContext(LanguageContext);
  const { eyebrow, title, description, locations } = locationData[language];
  const isRTL = language === "ar";

  const sectionRef = useRef(null);
  const headerRef  = useRef(null);
  const cardsRef   = useRef([]);

  // ── Header + Cards animation (نفس نظام Amenities) ──
  useEffect(() => {
    const section = sectionRef.current;
    const header  = headerRef.current;
    const cards   = cardsRef.current.filter(Boolean);
    if (!section || !header) return;

    // reset
    gsap.set(header, { opacity: 0, y: 35 });
    gsap.set(cards,  { opacity: 0, y: 30 });

    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          gsap.to(header, { opacity: 1, y: 0, duration: 0.8, ease: "power3.out" });
          gsap.to(cards,  { opacity: 1, y: 0, duration: 0.6, ease: "power3.out",
            stagger: 0.1, delay: 0.25 });
        } else {
          gsap.set(header, { opacity: 0, y: 35 });
          gsap.set(cards,  { opacity: 0, y: 30 });
        }
      });
    }, { threshold: 0.1 });

    obs.observe(section);
    return () => obs.disconnect();
  }, [language]);

  return (
    <section
      id="location"
      ref={sectionRef}
      dir={isRTL ? "rtl" : "ltr"}
      style={{
        position:   "relative",
        padding:    "90px 0 80px",
        background: "linear-gradient(170deg, #f0f7f0 0%, #ffffff 55%, #f4f8f4 100%)",
        fontFamily: "'Cairo', sans-serif",
        overflow:   "hidden",
      }}
    >
      {/* ── Wavy top edge ── */}
      <div style={{
        position: "absolute", top: "-58px", left: 0, right: 0,
        height: "80px", overflow: "hidden", zIndex: 1, pointerEvents: "none",
      }}>
        <svg viewBox="0 0 1440 80" preserveAspectRatio="none"
          style={{ width: "100%", height: "100%", display: "block" }}>
          <path d="M0,0 L1440,0 L1440,25 Q1260,80 1080,42 Q900,8 720,50 Q540,88 360,42 Q180,0 0,50 Z"
            fill="#ffffff" />
        </svg>
      </div>

      <div className="container" style={{ position: "relative", zIndex: 2, padding: "0 24px" }}>

        {/* ── Header ── */}
        <div ref={headerRef} style={{ textAlign: "center", marginBottom: "56px", opacity: 0 }}>
          <div style={{
            display: "flex", alignItems: "center",
            justifyContent: "center", gap: "10px", marginBottom: "14px",
          }}>
            <span style={{
              display: "inline-block", width: "36px", height: "2px",
              background: "linear-gradient(to right, transparent, #4a7c3f)",
            }} />
            <span style={{
              fontSize: "0.75rem", fontWeight: 700,
              letterSpacing: "0.18em", textTransform: "uppercase", color: "#4a7c3f",
            }}>
              📍 {eyebrow}
            </span>
            <span style={{
              display: "inline-block", width: "36px", height: "2px",
              background: "linear-gradient(to left, transparent, #4a7c3f)", opacity: 0.6,
            }} />
          </div>

          <h2 style={{
            fontWeight: 900, fontSize: "clamp(1.9rem, 4.5vw, 3rem)",
            color: "#0f1f0f", lineHeight: 1.2, marginBottom: "14px",
          }}>
            {title}
          </h2>

          <p style={{
            maxWidth: "580px", margin: "0 auto",
            fontSize: "clamp(0.92rem, 2vw, 1.02rem)",
            color: "#4a5a4a", lineHeight: 1.85,
          }}>
            {description}
          </p>
        </div>

        {/* ── Cards ── */}
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
          gap: "16px", maxWidth: "1100px", margin: "0 auto",
        }}>
          {locations.map(({ Icon, time, unit, place }, i) => (
            <div
              key={i}
              ref={(el) => (cardsRef.current[i] = el)}
              onMouseEnter={e => {
                e.currentTarget.style.transform   = "translateY(-5px)";
                e.currentTarget.style.boxShadow   = "0 20px 48px rgba(74,124,63,0.15)";
                e.currentTarget.style.borderColor = "rgba(74,124,63,0.35)";
              }}
              onMouseLeave={e => {
                e.currentTarget.style.transform   = "translateY(0)";
                e.currentTarget.style.boxShadow   = "0 2px 16px rgba(0,0,0,0.06)";
                e.currentTarget.style.borderColor = "rgba(74,124,63,0.12)";
              }}
              style={{
                display: "flex", alignItems: "center", gap: "18px",
                borderRadius: "16px",
                border: "1px solid rgba(74,124,63,0.12)",
                background: "rgba(255,255,255,0.9)",
                backdropFilter: "blur(10px)",
                boxShadow: "0 2px 16px rgba(0,0,0,0.06)",
                padding: "20px 24px",
                cursor: "default",
                opacity: 0,
                transition: "transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease",
              }}
            >
              {/* Icon box */}
              <div style={{
                flexShrink: 0, width: "52px", height: "52px", borderRadius: "14px",
                background: "linear-gradient(135deg, rgba(74,124,63,0.1), rgba(106,173,85,0.18))",
                border: "1px solid rgba(74,124,63,0.18)",
                display: "flex", alignItems: "center", justifyContent: "center",
                color: "#4a7c3f",
              }}>
                <Icon sx={{ fontSize: "1.6rem" }} />
              </div>

              {/* Text */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontSize: "clamp(0.88rem, 1.8vw, 0.95rem)", fontWeight: 600,
                  color: "#0f1f0f", marginBottom: "3px",
                  whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                }}>
                  {place}
                </div>
                <div style={{ fontSize: "0.8rem", color: "#4a5a4a", fontWeight: 400 }}>
                  {isRTL ? `على بُعد ${time} ${unit}` : `${time} ${unit} away`}
                </div>
              </div>

              {/* Time pill */}
              <div style={{
                flexShrink: 0, display: "flex", flexDirection: "column",
                alignItems: "center", justifyContent: "center",
                width: "48px", height: "48px", borderRadius: "12px",
                background: "linear-gradient(135deg, #4a7c3f, #6aad55)",
                color: "#fff", fontWeight: 800, fontSize: "1rem", lineHeight: 1,
              }}>
                <span>{time}</span>
                <span style={{ fontSize: "0.6rem", fontWeight: 500, opacity: 0.85, marginTop: "2px" }}>
                  {unit}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default LocationSection;
